import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Loader2, Mail, ExternalLink, Send, RotateCw, Search, X,
  ShieldCheck, BellRing, Tag, MessageSquare, AlertTriangle, Trash2,
  Rocket,
} from "lucide-react";

// Small inline error banner used inside each section that loads stats —
// surfaces axios errors WITHOUT unmounting siblings, with a retry button.
function SectionError({ label, message, onRetry, testid }) {
  return (
    <div
      className="flex items-start gap-3 border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-zinc-200 px-3 py-2 text-xs font-mono"
      data-testid={testid}
    >
      <AlertTriangle className="text-[#F59E0B] shrink-0 mt-0.5" size={13} />
      <div className="flex-1">
        <div className="text-[#F59E0B] uppercase tracking-widest text-[10px]">
          {label} failed
        </div>
        <div className="mt-0.5 text-zinc-300 break-all">{message}</div>
      </div>
      <button
        onClick={onRetry}
        className="border border-zinc-700 hover:border-[#00E5FF] hover:text-[#00E5FF] text-zinc-300 px-2 py-1 uppercase tracking-widest text-[10px] flex items-center gap-1"
        data-testid={`${testid}-retry`}
      >
        <RotateCw size={10} /> retry
      </button>
    </div>
  );
}

// ============================================================
// Emails admin tab
// ============================================================
// Section 1 — template previews + "send test to me"
// Section 2 — inactivity nudge (eligible count, dry run, send, batch log)
// Section 3 — EARLYBIRD-style promo blast (coupon + discount + dry run + send)
// Section 4 — manual single email to a chosen user
// ============================================================
export default function EmailsTab({ adminEmail }) {
  const [err, setErr] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  return (
    <div data-testid="admin-emails-tab" className="space-y-10">
      {err && (
        <div
          data-testid="emails-tab-banner"
          className="flex items-start gap-3 border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-zinc-200 px-4 py-3 text-xs font-mono"
        >
          <AlertTriangle className="text-[#F59E0B] shrink-0 mt-0.5" size={14} />
          <div className="flex-1">
            <div className="text-[#F59E0B] uppercase tracking-widest text-[10px]">one section failed to load</div>
            <div className="mt-0.5 text-zinc-300 break-all">{err}</div>
            <div className="mt-1 text-zinc-500">Other sections below should still work — refresh the tab to retry.</div>
          </div>
          <button
            onClick={() => setErr(null)}
            className="text-zinc-500 hover:text-zinc-200"
            aria-label="dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}
      <TemplatePreviewSection backendUrl={REACT_APP_BACKEND_URL} setErr={setErr} adminEmail={adminEmail} />
      <FirstLabSection setErr={setErr} setConfirm={setConfirm} />
      <NudgeSection setErr={setErr} setConfirm={setConfirm} />
      <PromoSection setErr={setErr} setConfirm={setConfirm} />
      <ManualSendSection setErr={setErr} setConfirm={setConfirm} />

      {confirm && (
        <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />
      )}
    </div>
  );
}

// ============================================================
// Section 1 — Template previews + test sends
// ============================================================
const TEMPLATES = [
  { kind: "verify",    icon: ShieldCheck,    name: "Verification email", desc: "Sent on signup. Contains the email-verification link." },
  { kind: "welcome",   icon: Mail,           name: "Welcome email",      desc: "Sent immediately after a user verifies their email." },
  { kind: "first_lab", icon: Rocket,         name: "First-lab nudge",    desc: "Action-focused re-engagement for users who verified but never started a lab." },
  { kind: "nudge",     icon: BellRing,       name: "Inactivity nudge",   desc: "One-time reminder for verified users with no lab activity." },
  { kind: "promo",     icon: Tag,            name: "Upgrade promo",      desc: "Discount email for free-tier users who hit the labs cap." },
];

function TemplatePreviewSection({ backendUrl, setErr, adminEmail }) {
  const [busy, setBusy] = useState(null);
  // Per-card "send test" mini-form state. Keyed by template kind so each
  // card can independently show/hide its recipient input.
  const [openForm, setOpenForm] = useState(null);
  const [toEmail, setToEmail] = useState("");

  const openPreview = (kind) => {
    // Server requires an auth cookie/Bearer to view this — the API client
    // we use sends the access_token cookie cross-origin, so a plain new-tab
    // navigation to the URL works.
    window.open(`${backendUrl}/api/admin/email/preview?kind=${kind}`,
                "_blank", "noopener");
  };

  const startTest = (kind) => () => {
    if (openForm === kind) { setOpenForm(null); return; }
    setOpenForm(kind);
    setToEmail(adminEmail || "");
  };

  const sendTest = (kind) => async () => {
    const recipient = (toEmail || adminEmail || "").trim();
    if (!recipient) { toast.error("Enter a recipient email."); return; }
    setBusy(kind);
    try {
      const { data } = await api.post("/admin/email/test-send", {
        kind, to: recipient,
      });
      toast.success(`Test ${kind} email sent to ${data.to}`);
      setOpenForm(null);
    } catch (e) {
      setErr(null);
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section data-testid="emails-section-templates">
      <SectionHeader title="Email templates" subtitle="Preview each transactional template or send a test copy to any address." />
      <div className="grid sm:grid-cols-2 gap-3">
        {TEMPLATES.map((t) => {
          const Icon = t.icon;
          const isBusy = busy === t.kind;
          const isOpen = openForm === t.kind;
          return (
            <div key={t.kind} data-testid={`template-card-${t.kind}`}
              className="bg-[#141416] border border-zinc-900 p-4 flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <Icon size={18} className="text-[#00E5FF] shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-zinc-100">{t.name}</div>
                  <div className="text-[11px] text-zinc-500 font-mono mt-0.5">{t.desc}</div>
                </div>
              </div>
              <div className="flex gap-2 mt-1">
                <button
                  onClick={() => openPreview(t.kind)}
                  data-testid={`preview-${t.kind}`}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-zinc-800 text-zinc-300 hover:border-[#00E5FF] hover:text-[#00E5FF] text-[10px] uppercase tracking-widest font-mono transition-colors"
                >
                  <ExternalLink size={11} /> preview
                </button>
                <button
                  onClick={startTest(t.kind)}
                  disabled={isBusy}
                  data-testid={`test-send-${t.kind}`}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 border text-[10px] uppercase tracking-widest font-mono transition-colors disabled:opacity-50 ${
                    isOpen
                      ? "bg-[#00E5FF] text-black border-[#00E5FF]"
                      : "bg-[#00E5FF]/10 border-[#00E5FF]/50 text-[#00E5FF] hover:bg-[#00E5FF] hover:text-black"
                  }`}
                >
                  <Send size={11} />
                  send test
                </button>
              </div>

              {isOpen && (
                <div className="border border-zinc-800 bg-black p-3 space-y-2" data-testid={`test-form-${t.kind}`}>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">
                    recipient
                  </div>
                  <input
                    value={toEmail}
                    onChange={(e) => setToEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && sendTest(t.kind)()}
                    placeholder="you@example.com"
                    type="email"
                    data-testid={`test-to-${t.kind}`}
                    className="w-full bg-black border border-zinc-800 px-3 py-2 text-xs font-mono focus:border-[#00E5FF] outline-none"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => setOpenForm(null)}
                      data-testid={`test-cancel-${t.kind}`}
                      className="px-3 py-1.5 border border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 text-[10px] uppercase tracking-widest font-mono transition-colors"
                    >
                      cancel
                    </button>
                    <button
                      onClick={sendTest(t.kind)}
                      disabled={isBusy || !toEmail.trim()}
                      data-testid={`test-confirm-${t.kind}`}
                      className="flex items-center gap-2 px-3 py-1.5 bg-[#00E5FF] text-black border border-[#00E5FF] hover:bg-[#00b8cc] text-[10px] uppercase tracking-widest font-mono transition-colors disabled:opacity-40"
                    >
                      {isBusy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                      send
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ============================================================
// Section 2a — First-lab re-engagement (action-focused, separate from nudge)
// ============================================================
function FirstLabSection({ setErr, setConfirm }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editor, setEditor] = useState(null);
  const [eligible, setEligible] = useState([]);
  const [unknown, setUnknown] = useState([]);
  const [addInput, setAddInput] = useState("");
  const [loadErr, setLoadErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const { data } = await api.get("/admin/email/first-lab-stats");
      setStats(data);
    } catch (e) {
      setLoadErr(e?.response?.data?.detail || e.message || "Network error");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEditor = async () => {
    setBusy(true);
    setUnknown([]);
    try {
      const { data } = await api.post("/admin/email/scan-first-lab?dry_run=true");
      const ems = (data.eligible || []).map((u) => u.email);
      setEligible(data.eligible || []);
      setEditor({ selected: new Set(ems), extra: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const closeEditor = () => { setEditor(null); setAddInput(""); setUnknown([]); };

  const toggleEmail = (em) => {
    setEditor((ed) => {
      const next = new Set(ed.selected);
      if (next.has(em)) next.delete(em); else next.add(em);
      return { ...ed, selected: next };
    });
  };

  const removeExtra = (em) => {
    setEditor((ed) => ({
      ...ed,
      extra: ed.extra.filter((e) => e !== em),
      selected: new Set([...ed.selected].filter((e) => e !== em)),
    }));
  };

  const addExtraEmails = () => {
    const tokens = addInput.split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (tokens.length === 0) return;
    setEditor((ed) => {
      const sel = new Set(ed.selected);
      const ex = new Set(ed.extra);
      for (const t of tokens) {
        if (!sel.has(t)) sel.add(t);
        if (!eligible.some((u) => u.email === t)) ex.add(t);
      }
      return { selected: sel, extra: Array.from(ex) };
    });
    setAddInput("");
  };

  const finalEmails = editor ? Array.from(editor.selected) : [];

  const selectAll = (visible) => setEditor((ed) => {
    const next = new Set(ed.selected);
    for (const em of visible) next.add(em);
    return { ...ed, selected: next };
  });
  const selectNone = (visible) => setEditor((ed) => {
    const next = new Set(ed.selected);
    for (const em of visible) next.delete(em);
    return { ...ed, selected: next };
  });
  const invertSelection = (visible) => setEditor((ed) => {
    const next = new Set(ed.selected);
    for (const em of visible) {
      if (next.has(em)) next.delete(em); else next.add(em);
    }
    return { ...ed, selected: next };
  });

  const doSend = () => {
    const n = finalEmails.length;
    if (n === 0) { toast.message("Pick at least one recipient."); return; }
    setConfirm({
      title: "Send first-lab email",
      message: `Send first-lab nudge to ${n} user${n === 1 ? "" : "s"}? This cannot be undone — each user only ever receives this once.`,
      destructive: false,
      confirmLabel: `Send to ${n}`,
      onConfirm: async () => {
        setBusy(true);
        try {
          const { data } = await api.post(
            "/admin/email/scan-first-lab?dry_run=false",
            { emails: finalEmails }
          );
          toast.success(`Sent ${data.sent.length} · failed ${data.failed.length}`);
          setUnknown(data.unknown || []);
          if ((data.unknown || []).length > 0) {
            toast.warning(`${data.unknown.length} unknown email${data.unknown.length === 1 ? "" : "s"} skipped`);
          }
          closeEditor();
          await load();
        } catch (e) {
          toast.error(e?.response?.data?.detail || e.message);
        } finally {
          setBusy(false);
          setConfirm(null);
        }
      },
    });
  };

  return (
    <section data-testid="emails-section-first-lab">
      <SectionHeader title="First-lab nudge" subtitle="Action-focused re-engagement for verified users who haven't started a lab. Each user receives this at most once." />

      {loadErr && (
        <div className="mb-3">
          <SectionError label="first-lab stats" message={loadErr} onRetry={load} testid="first-lab-error" />
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3 mb-3">
        <Tile label="eligible users" value={loading ? "…" : (stats?.eligible_count ?? 0)} color="text-zinc-100" testid="first-lab-eligible-count" />
        <Tile label="batches sent" value={loading ? "…" : (stats?.batches?.length ?? 0)} color="text-zinc-400" />
        <Tile label="last batch" value={loading ? "…" : (stats?.batches?.[0]?.sent_count ?? "—")} color="text-zinc-400" testid="first-lab-last-batch" />
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <ActionBtn onClick={openEditor} disabled={busy} testid="first-lab-dry-run">
          <Send size={11} /> send to…
        </ActionBtn>
      </div>

      {editor && (
        <RecipientsEditor
          testid="first-lab-editor"
          title={`Recipients · ${finalEmails.length} selected`}
          eligible={eligible}
          selected={editor.selected}
          extras={editor.extra}
          unknown={unknown}
          addInput={addInput}
          onAddInputChange={setAddInput}
          onAdd={addExtraEmails}
          onToggle={toggleEmail}
          onRemoveExtra={removeExtra}
          onClose={closeEditor}
          onSend={doSend}
          busy={busy}
          sendLabel="Send first-lab"
          onSelectAll={selectAll}
          onSelectNone={selectNone}
          onInvert={invertSelection}
        />
      )}

      <BatchLog
        testid="first-lab-batch-log"
        rows={stats?.batches || []}
        columns={["sent_at", "sent_count", "failed_count", "actor"]}
        headers={["sent at", "sent", "failed", "actor"]}
      />
    </section>
  );
}

// ============================================================
// Section 2 — Inactivity nudge
// ============================================================
function NudgeSection({ setErr, setConfirm }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Recipient editor state. `editor=null` means closed. Otherwise:
  //   { selected: Set<email>, extra: string[] }  — extras are admin-added emails
  //   that don't appear in the auto-eligible list.
  const [editor, setEditor] = useState(null);
  const [eligible, setEligible] = useState([]);
  const [unknown, setUnknown] = useState([]);
  const [addInput, setAddInput] = useState("");
  const [loadErr, setLoadErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const { data } = await api.get("/admin/email/nudge-stats");
      setStats(data);
    } catch (e) {
      setLoadErr(e?.response?.data?.detail || e.message || "Network error");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEditor = async () => {
    setBusy(true);
    setUnknown([]);
    try {
      // Plain dry-run — no explicit emails yet; we want the full auto-eligible
      // list so the admin can de-select people.
      const { data } = await api.post("/admin/email/scan-nudges?dry_run=true");
      const ems = (data.eligible || []).map((u) => u.email);
      setEligible(data.eligible || []);
      setEditor({
        selected: new Set(ems),
        extra: [],
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const closeEditor = () => { setEditor(null); setAddInput(""); setUnknown([]); };

  const toggleEmail = (em) => {
    setEditor((ed) => {
      const next = new Set(ed.selected);
      if (next.has(em)) next.delete(em); else next.add(em);
      return { ...ed, selected: next };
    });
  };

  const removeExtra = (em) => {
    setEditor((ed) => ({
      ...ed,
      extra: ed.extra.filter((e) => e !== em),
      selected: new Set([...ed.selected].filter((e) => e !== em)),
    }));
  };

  const addExtraEmails = () => {
    const tokens = addInput.split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (tokens.length === 0) return;
    setEditor((ed) => {
      const sel = new Set(ed.selected);
      const ex = new Set(ed.extra);
      for (const t of tokens) {
        if (!sel.has(t)) sel.add(t);
        // Track as "extra" only if not part of the auto-eligible list.
        if (!eligible.some((u) => u.email === t)) ex.add(t);
      }
      return { selected: sel, extra: Array.from(ex) };
    });
    setAddInput("");
  };

  const finalEmails = editor ? Array.from(editor.selected) : [];

  // Quick-select operates on whatever the editor passes in (the currently
  // filtered/visible emails) so combining filter + all/none/invert lets the
  // admin scope changes to a subset without nuking the rest.
  const selectAll = (visible) => setEditor((ed) => {
    const next = new Set(ed.selected);
    for (const em of visible) next.add(em);
    return { ...ed, selected: next };
  });
  const selectNone = (visible) => setEditor((ed) => {
    const next = new Set(ed.selected);
    for (const em of visible) next.delete(em);
    return { ...ed, selected: next };
  });
  const invertSelection = (visible) => setEditor((ed) => {
    const next = new Set(ed.selected);
    for (const em of visible) {
      if (next.has(em)) next.delete(em); else next.add(em);
    }
    return { ...ed, selected: next };
  });

  const doSend = () => {
    const n = finalEmails.length;
    if (n === 0) {
      toast.message("Pick at least one recipient.");
      return;
    }
    setConfirm({
      title: "Send inactivity nudge",
      message: `Send nudge to ${n} user${n === 1 ? "" : "s"}? This cannot be undone.`,
      destructive: false,
      confirmLabel: `Send to ${n}`,
      onConfirm: async () => {
        setBusy(true);
        try {
          const { data } = await api.post(
            "/admin/email/scan-nudges?dry_run=false",
            { emails: finalEmails }
          );
          toast.success(`Sent ${data.sent.length} · failed ${data.failed.length}`);
          setUnknown(data.unknown || []);
          if ((data.unknown || []).length > 0) {
            toast.warning(`${data.unknown.length} unknown email${data.unknown.length === 1 ? "" : "s"} skipped`);
          }
          closeEditor();
          await load();
        } catch (e) {
          toast.error(e?.response?.data?.detail || e.message);
        } finally {
          setBusy(false);
          setConfirm(null);
        }
      },
    });
  };

  return (
    <section data-testid="emails-section-nudge">
      <SectionHeader title="Inactivity nudge" subtitle="One-time reminder for verified users with zero lab activity. Each user receives this at most once." />

      {loadErr && (
        <div className="mb-3">
          <SectionError label="nudge stats" message={loadErr} onRetry={load} testid="nudge-error" />
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3 mb-3">
        <Tile label="eligible users" value={loading ? "…" : (stats?.eligible_count ?? 0)} color="text-zinc-100" testid="nudge-eligible-count" />
        <Tile label="batches sent" value={loading ? "…" : (stats?.batches?.length ?? 0)} color="text-zinc-400" />
        <Tile label="last batch" value={loading ? "…" : (stats?.batches?.[0]?.sent_count ?? "—")} color="text-zinc-400" testid="nudge-last-batch" />
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <ActionBtn onClick={openEditor} disabled={busy} testid="nudge-dry-run">
          <Send size={11} /> send to…
        </ActionBtn>
      </div>

      {editor && (
        <RecipientsEditor
          testid="nudge-editor"
          title={`Recipients · ${finalEmails.length} selected`}
          eligible={eligible}
          selected={editor.selected}
          extras={editor.extra}
          unknown={unknown}
          addInput={addInput}
          onAddInputChange={setAddInput}
          onAdd={addExtraEmails}
          onToggle={toggleEmail}
          onRemoveExtra={removeExtra}
          onClose={closeEditor}
          onSend={doSend}
          busy={busy}
          sendLabel="Send nudge"
          onSelectAll={selectAll}
          onSelectNone={selectNone}
          onInvert={invertSelection}
        />
      )}

      <BatchLog
        testid="nudge-batch-log"
        rows={stats?.batches || []}
        columns={["sent_at", "sent_count", "failed_count", "actor"]}
        headers={["sent at", "sent", "failed", "actor"]}
      />
    </section>
  );
}

// ============================================================
// Section 3 — Promo blast
// ============================================================
function PromoSection({ setErr, setConfirm }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("EARLYBIRD");
  const [discount, setDiscount] = useState("50% off");
  const [busy, setBusy] = useState(false);
  const [editor, setEditor] = useState(null);
  const [eligible, setEligible] = useState([]);
  const [unknown, setUnknown] = useState([]);
  const [addInput, setAddInput] = useState("");
  const [loadErr, setLoadErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const { data } = await api.get("/admin/email/promo-stats");
      setStats(data);
    } catch (e) {
      setLoadErr(e?.response?.data?.detail || e.message || "Network error");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEditor = async () => {
    setBusy(true);
    setUnknown([]);
    try {
      const { data } = await api.post("/admin/email/scan-promos",
        { code, discount, dry_run: true });
      const ems = (data.eligible || []).map((u) => u.email);
      setEligible(data.eligible || []);
      setEditor({ selected: new Set(ems), extra: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const closeEditor = () => { setEditor(null); setAddInput(""); setUnknown([]); };

  const toggleEmail = (em) => {
    setEditor((ed) => {
      const next = new Set(ed.selected);
      if (next.has(em)) next.delete(em); else next.add(em);
      return { ...ed, selected: next };
    });
  };

  const removeExtra = (em) => {
    setEditor((ed) => ({
      ...ed,
      extra: ed.extra.filter((e) => e !== em),
      selected: new Set([...ed.selected].filter((e) => e !== em)),
    }));
  };

  const addExtraEmails = () => {
    const tokens = addInput.split(/[\s,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (tokens.length === 0) return;
    setEditor((ed) => {
      const sel = new Set(ed.selected);
      const ex = new Set(ed.extra);
      for (const t of tokens) {
        if (!sel.has(t)) sel.add(t);
        if (!eligible.some((u) => u.email === t)) ex.add(t);
      }
      return { selected: sel, extra: Array.from(ex) };
    });
    setAddInput("");
  };

  const finalEmails = editor ? Array.from(editor.selected) : [];

  const selectAll = (visible) => setEditor((ed) => {
    const next = new Set(ed.selected);
    for (const em of visible) next.add(em);
    return { ...ed, selected: next };
  });
  const selectNone = (visible) => setEditor((ed) => {
    const next = new Set(ed.selected);
    for (const em of visible) next.delete(em);
    return { ...ed, selected: next };
  });
  const invertSelection = (visible) => setEditor((ed) => {
    const next = new Set(ed.selected);
    for (const em of visible) {
      if (next.has(em)) next.delete(em); else next.add(em);
    }
    return { ...ed, selected: next };
  });

  const doSend = () => {
    const n = finalEmails.length;
    if (n === 0) { toast.message("Pick at least one recipient."); return; }
    setConfirm({
      title: "Send promo email",
      message: `Send promo email to ${n} user${n === 1 ? "" : "s"} with code ${code.toUpperCase()} (${discount})? This cannot be undone.`,
      destructive: false,
      confirmLabel: `Send to ${n}`,
      onConfirm: async () => {
        setBusy(true);
        try {
          const { data } = await api.post(
            "/admin/email/scan-promos",
            { code, discount, dry_run: false, emails: finalEmails }
          );
          toast.success(`Sent ${data.sent.length} · failed ${data.failed.length}`);
          setUnknown(data.unknown || []);
          if ((data.unknown || []).length > 0) {
            toast.warning(`${data.unknown.length} unknown email${data.unknown.length === 1 ? "" : "s"} skipped`);
          }
          closeEditor();
          await load();
        } catch (e) {
          toast.error(e?.response?.data?.detail || e.message);
        } finally {
          setBusy(false);
          setConfirm(null);
        }
      },
    });
  };

  return (
    <section data-testid="emails-section-promo">
      <SectionHeader title="Upgrade promo blast" subtitle="Send a discount code to free-tier users who exhausted their labs and haven't upgraded. Each recipient is flagged so they only receive it once." />

      {loadErr && (
        <div className="mb-3">
          <SectionError label="promo stats" message={loadErr} onRetry={load} testid="promo-error" />
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3 mb-3">
        <Tile label="eligible users" value={loading ? "…" : (stats?.eligible_count ?? 0)} color="text-zinc-100" testid="promo-eligible-count" />
        <Tile label="batches sent" value={loading ? "…" : (stats?.batches?.length ?? 0)} color="text-zinc-400" />
        <Tile label="last batch" value={loading ? "…" : (stats?.batches?.[0]?.sent_count ?? "—")} color="text-zinc-400" testid="promo-last-batch" />
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <Field label="coupon code">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            data-testid="promo-code"
            className="w-full bg-black border border-zinc-800 px-3 py-2 text-xs font-mono uppercase tracking-widest focus:border-[#00E5FF] outline-none"
          />
        </Field>
        <Field label="discount">
          <input
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            data-testid="promo-discount"
            placeholder="50% off"
            className="w-full bg-black border border-zinc-800 px-3 py-2 text-xs font-mono focus:border-[#00E5FF] outline-none"
          />
        </Field>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <ActionBtn onClick={openEditor} disabled={busy} testid="promo-dry-run">
          <Send size={11} /> send to…
        </ActionBtn>
      </div>

      {editor && (
        <RecipientsEditor
          testid="promo-editor"
          title={`Recipients · ${finalEmails.length} selected · code ${code.toUpperCase()} · ${discount}`}
          eligible={eligible}
          selected={editor.selected}
          extras={editor.extra}
          unknown={unknown}
          addInput={addInput}
          onAddInputChange={setAddInput}
          onAdd={addExtraEmails}
          onToggle={toggleEmail}
          onRemoveExtra={removeExtra}
          onClose={closeEditor}
          onSend={doSend}
          busy={busy}
          sendLabel="Send promo"
          onSelectAll={selectAll}
          onSelectNone={selectNone}
          onInvert={invertSelection}
        />
      )}

      <BatchLog
        testid="promo-batch-log"
        rows={stats?.batches || []}
        columns={["sent_at", "code", "discount", "sent_count", "failed_count", "actor"]}
        headers={["sent at", "code", "discount", "sent", "failed", "actor"]}
      />
    </section>
  );
}

// ============================================================
// Section 4 — Manual single email
// ============================================================
function ManualSendSection({ setErr, setConfirm }) {  // eslint-disable-line no-unused-vars
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [kind, setKind] = useState("welcome");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [code, setCode] = useState("EARLYBIRD");
  const [discount, setDiscount] = useState("50% off");
  const [busy, setBusy] = useState(false);
  const [searching, setSearching] = useState(false);

  const search = async () => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get("/admin/users/search", { params: { q } });
      setResults(data.results || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSearching(false); }
  };

  const doSend = () => {
    if (!selected) { toast.error("Select a user first."); return; }
    if (kind === "custom" && (!subject.trim() || !body.trim())) {
      toast.error("Custom emails require both subject and body.");
      return;
    }
    setConfirm({
      title: "Send manual email",
      message: `Send a ${kind} email to ${selected.email}?`,
      destructive: false,
      confirmLabel: "Send email",
      onConfirm: async () => {
        setBusy(true);
        try {
          const payload = {
            user_id: selected.id,
            kind,
            ...(kind === "custom" ? { subject, body } : {}),
            ...(kind === "promo" ? { code, discount } : {}),
          };
          const { data } = await api.post("/admin/email/manual-send", payload);
          toast.success(`Sent ${data.kind} email to ${data.to}`);
        } catch (e) {
          toast.error(e?.response?.data?.detail || e.message);
        } finally {
          setBusy(false);
          setConfirm(null);
        }
      },
    });
  };

  const needsPromoFields = kind === "promo";
  const needsCustomFields = kind === "custom";

  return (
    <section data-testid="emails-section-manual">
      <SectionHeader title="Manual single email" subtitle="Send any template (or a custom one) to a specific user. Doesn't affect bulk-send flags." />

      <div className="bg-[#141416] border border-zinc-900 p-4 space-y-4">
        {/* User picker */}
        <div>
          <Field label="recipient">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && search()}
                  placeholder="search by email…"
                  data-testid="manual-search-input"
                  className="w-full bg-black border border-zinc-800 pl-8 pr-3 py-2 text-xs font-mono focus:border-[#00E5FF] outline-none"
                />
              </div>
              <button
                onClick={search}
                disabled={searching}
                data-testid="manual-search-btn"
                className="px-3 py-2 border border-zinc-800 text-zinc-300 hover:border-[#00E5FF] hover:text-[#00E5FF] text-[10px] uppercase tracking-widest font-mono transition-colors disabled:opacity-50"
              >
                {searching ? <Loader2 size={11} className="animate-spin" /> : "search"}
              </button>
            </div>
          </Field>

          {results.length > 0 && !selected && (
            <ul data-testid="manual-search-results"
              className="border border-zinc-900 mt-2 divide-y divide-zinc-900 max-h-48 overflow-y-auto">
              {results.map((u) => (
                <li key={u.id}>
                  <button
                    onClick={() => { setSelected(u); setResults([]); setQ(""); }}
                    data-testid={`manual-pick-${u.id}`}
                    className="w-full text-left px-3 py-2 text-xs font-mono hover:bg-zinc-900/40 flex items-center justify-between gap-2"
                  >
                    <span className="text-zinc-100 truncate">{u.email}</span>
                    <span className={u.tier === "pro" ? "text-[#00E5FF] text-[10px] uppercase tracking-widest" : "text-zinc-500 text-[10px] uppercase tracking-widest"}>{u.tier || "free"}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {selected && (
            <div data-testid="manual-selected"
              className="mt-2 flex items-center justify-between gap-2 bg-[#0A0A0A] border border-[#00E5FF]/40 px-3 py-2">
              <div className="text-xs font-mono text-zinc-100">
                <span className="text-[10px] text-[#00E5FF] uppercase tracking-widest mr-2">to</span>
                {selected.email}
                <span className="text-zinc-500 ml-2 text-[10px] uppercase tracking-widest">{selected.tier || "free"}</span>
              </div>
              <button onClick={() => setSelected(null)} className="text-zinc-500 hover:text-zinc-200" data-testid="manual-clear-selected">
                <X size={14} />
              </button>
            </div>
          )}
        </div>

        {/* Template picker */}
        <Field label="template">
          <div className="flex flex-wrap gap-2" data-testid="manual-kind-picker">
            {[
              { v: "welcome",   label: "welcome" },
              { v: "nudge",     label: "nudge" },
              { v: "first_lab", label: "first lab" },
              { v: "promo",     label: "promo" },
              { v: "custom",    label: "custom" },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setKind(opt.v)}
                data-testid={`manual-kind-${opt.v}`}
                className={`px-3 py-2 text-[10px] uppercase tracking-widest font-mono border transition-colors ${
                  kind === opt.v
                    ? "bg-[#00E5FF] text-black border-[#00E5FF]"
                    : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </Field>

        {needsPromoFields && (
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="coupon code">
              <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                data-testid="manual-promo-code"
                className="w-full bg-black border border-zinc-800 px-3 py-2 text-xs font-mono uppercase tracking-widest focus:border-[#00E5FF] outline-none" />
            </Field>
            <Field label="discount">
              <input value={discount} onChange={(e) => setDiscount(e.target.value)}
                data-testid="manual-promo-discount"
                className="w-full bg-black border border-zinc-800 px-3 py-2 text-xs font-mono focus:border-[#00E5FF] outline-none" />
            </Field>
          </div>
        )}

        {needsCustomFields && (
          <>
            <Field label="subject">
              <input value={subject} onChange={(e) => setSubject(e.target.value)}
                data-testid="manual-custom-subject"
                placeholder="A note from HypexLabs"
                className="w-full bg-black border border-zinc-800 px-3 py-2 text-xs font-mono focus:border-[#00E5FF] outline-none" />
            </Field>
            <Field label="body">
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6}
                data-testid="manual-custom-body"
                placeholder="Plain text. Blank lines start a new paragraph."
                className="w-full bg-black border border-zinc-800 px-3 py-2 text-xs font-mono leading-relaxed focus:border-[#00E5FF] outline-none resize-y" />
            </Field>
          </>
        )}

        <div className="flex justify-end">
          <ActionBtn variant="primary" onClick={doSend} disabled={busy || !selected} testid="manual-send-btn">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            send email
          </ActionBtn>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Shared bits
// ============================================================
function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-4">
      <h2 className="font-display font-black text-xl tracking-tight">{title}</h2>
      {subtitle && (
        <p className="text-[11px] text-zinc-500 font-mono mt-1 leading-relaxed">
          {subtitle}
        </p>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function Tile({ label, value, color, testid }) {
  return (
    <div className="bg-[#141416] border border-zinc-900 px-4 py-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">{label}</div>
      <div className={`text-3xl font-display font-black mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function ActionBtn({ children, onClick, disabled, testid, variant = "default" }) {
  const cls = variant === "primary"
    ? "bg-[#00E5FF] text-black border-[#00E5FF] hover:bg-[#00b8cc]"
    : "border-zinc-800 text-zinc-300 hover:border-[#00E5FF] hover:text-[#00E5FF]";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2 border text-[10px] uppercase tracking-widest font-mono transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${cls}`}
    >
      {children}
    </button>
  );
}

function RecipientsEditor({
  testid, title, eligible, selected, extras, unknown,
  addInput, onAddInputChange, onAdd,
  onToggle, onRemoveExtra, onClose, onSend, busy, sendLabel,
  onSelectAll, onSelectNone, onInvert,
}) {
  const [filter, setFilter] = useState("");
  const f = filter.trim().toLowerCase();
  // Eligible rows + admin-added extras (extras shown at top so they're easy
  // to find/remove).
  const orderedExtras = extras.map((em) => ({ email: em, _extra: true }));
  const allRows = [...orderedExtras, ...eligible];
  const rows = f
    ? allRows.filter((u) => (u.email || "").toLowerCase().includes(f))
    : allRows;
  const visibleEmails = rows.map((u) => u.email);
  const total = selected.size;
  const visibleSelected = visibleEmails.filter((e) => selected.has(e)).length;

  return (
    <div data-testid={testid} className="border border-zinc-800 bg-[#141416] mb-3">
      <div className="flex justify-between items-center px-3 py-2 border-b border-zinc-800">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] font-mono">{title}</div>
        <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200" data-testid={`${testid}-close`}>
          <X size={12} />
        </button>
      </div>

      {/* Add by email */}
      <div className="px-3 py-3 border-b border-zinc-900 flex flex-wrap gap-2">
        <input
          value={addInput}
          onChange={(e) => onAddInputChange(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onAdd()}
          placeholder="add by email — paste comma/space-separated…"
          data-testid={`${testid}-add-input`}
          className="flex-1 min-w-[220px] bg-black border border-zinc-800 px-3 py-2 text-xs font-mono focus:border-[#00E5FF] outline-none"
        />
        <button
          onClick={onAdd}
          data-testid={`${testid}-add-btn`}
          className="px-3 py-2 border border-zinc-800 text-zinc-300 hover:border-[#00E5FF] hover:text-[#00E5FF] text-[10px] uppercase tracking-widest font-mono transition-colors"
        >
          add
        </button>
      </div>

      {/* Filter + quick-select toolbar */}
      <div className="px-3 py-2 border-b border-zinc-900 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="filter recipients by email…"
            data-testid={`${testid}-filter`}
            className="w-full bg-black border border-zinc-800 pl-7 pr-7 py-1.5 text-xs font-mono focus:border-[#00E5FF] outline-none"
          />
          {filter && (
            <button
              onClick={() => setFilter("")}
              data-testid={`${testid}-filter-clear`}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-200"
              title="clear"
            >
              <X size={11} />
            </button>
          )}
        </div>
        <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono ml-1">
          quick select
        </span>
        <button
          onClick={() => onSelectAll(visibleEmails)}
          data-testid={`${testid}-select-all`}
          className="px-2.5 py-1 border border-zinc-800 text-zinc-300 hover:border-[#00E5FF] hover:text-[#00E5FF] text-[10px] uppercase tracking-widest font-mono transition-colors"
        >
          all
        </button>
        <button
          onClick={() => onSelectNone(visibleEmails)}
          data-testid={`${testid}-select-none`}
          className="px-2.5 py-1 border border-zinc-800 text-zinc-300 hover:border-[#EF4444] hover:text-[#EF4444] text-[10px] uppercase tracking-widest font-mono transition-colors"
        >
          none
        </button>
        <button
          onClick={() => onInvert(visibleEmails)}
          data-testid={`${testid}-invert`}
          className="px-2.5 py-1 border border-zinc-800 text-zinc-300 hover:border-[#F59E0B] hover:text-[#F59E0B] text-[10px] uppercase tracking-widest font-mono transition-colors"
        >
          invert
        </button>
      </div>

      {f && (
        <div className="px-3 py-1.5 border-b border-zinc-900 text-[11px] font-mono text-zinc-500 flex items-center gap-2">
          <span>showing</span>
          <span className="text-zinc-300">{rows.length}</span>
          <span>of {allRows.length} · {visibleSelected} of {total} selected match this filter</span>
        </div>
      )}

      {/* List */}
      {allRows.length === 0 ? (
        <div className="px-3 py-6 text-center text-xs text-zinc-500 font-mono">
          no recipients yet — paste emails above to add some.
        </div>
      ) : rows.length === 0 ? (
        <div className="px-3 py-6 text-center text-xs text-zinc-500 font-mono">
          no recipients match this filter.
        </div>
      ) : (
        <ul className="divide-y divide-zinc-900 max-h-72 overflow-y-auto">
          {rows.map((u, i) => {
            const isExtra = !!u._extra;
            const checked = selected.has(u.email);
            return (
              <li key={`${u.email}-${i}`} className="px-3 py-2 flex items-center gap-3 hover:bg-zinc-900/40">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(u.email)}
                  data-testid={`${testid}-toggle-${u.email}`}
                  className="accent-[#00E5FF] shrink-0"
                />
                <span className="text-xs font-mono text-zinc-100 truncate flex-1">{u.email}</span>
                {isExtra && (
                  <span className="text-[10px] uppercase tracking-widest font-mono text-[#00E5FF]">added</span>
                )}
                {!isExtra && u.created_at && (
                  <span className="text-[10px] font-mono text-zinc-600">{(u.created_at || "").slice(0, 10)}</span>
                )}
                {isExtra && (
                  <button
                    onClick={() => onRemoveExtra(u.email)}
                    className="text-zinc-500 hover:text-[#EF4444]"
                    data-testid={`${testid}-remove-${u.email}`}
                    title="remove"
                  >
                    <X size={12} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {unknown && unknown.length > 0 && (
        <div className="px-3 py-2 border-t border-zinc-900 text-[11px] font-mono text-[#F59E0B]">
          {unknown.length} email{unknown.length === 1 ? " was" : "s were"} not found and skipped: {unknown.join(", ")}
        </div>
      )}

      {/* Footer */}
      <div className="px-3 py-3 border-t border-zinc-800 flex flex-wrap items-center justify-between gap-2">
        <div className="text-[11px] font-mono text-zinc-400">
          <span className="text-zinc-100">{total}</span> recipient{total === 1 ? "" : "s"} selected
          {extras.length > 0 && (
            <span className="text-zinc-500"> · {extras.length} added by you</span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            data-testid={`${testid}-cancel`}
            className="px-3 py-2 border border-zinc-800 text-zinc-400 hover:border-zinc-600 text-[10px] uppercase tracking-widest font-mono transition-colors"
          >
            cancel
          </button>
          <button
            onClick={onSend}
            disabled={busy || total === 0}
            data-testid={`${testid}-send`}
            className="flex items-center gap-2 px-4 py-2 bg-[#00E5FF] text-black border border-[#00E5FF] hover:bg-[#00b8cc] text-[10px] uppercase tracking-widest font-mono transition-colors disabled:opacity-40"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
            {sendLabel} ({total})
          </button>
        </div>
      </div>
    </div>
  );
}

function DryRunList({ label, items, onClose, testid }) {
  if (!items || items.length === 0) {
    return (
      <div data-testid={testid}
        className="border border-zinc-900 bg-[#141416] px-3 py-3 text-xs font-mono text-zinc-500 mb-3 flex justify-between items-center">
        <span>{label} — no rows to show.</span>
        <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={12} /></button>
      </div>
    );
  }
  return (
    <div data-testid={testid} className="border border-zinc-900 bg-[#141416] mb-3">
      <div className="flex justify-between items-center px-3 py-2 border-b border-zinc-900">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">{label}</div>
        <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={12} /></button>
      </div>
      <ul className="divide-y divide-zinc-900 max-h-56 overflow-y-auto">
        {items.map((u, i) => (
          <li key={i} className="px-3 py-1.5 text-xs font-mono text-zinc-300 flex justify-between gap-2">
            <span className="truncate">{u.email}</span>
            <span className="text-zinc-500">{(u.created_at || "").slice(0, 10)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BatchLog({ rows, columns, headers, testid }) {
  if (!rows || rows.length === 0) {
    return (
      <div data-testid={testid} className="text-[11px] text-zinc-600 font-mono">
        no batches sent yet.
      </div>
    );
  }
  return (
    <div data-testid={testid} className="border border-zinc-900 overflow-x-auto">
      <table className="w-full text-xs font-mono">
        <thead className="bg-[#141416] text-zinc-500 text-[10px] uppercase tracking-widest">
          <tr>
            {headers.map((h) => <th key={h} className="text-left px-3 py-2">{h}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-900">
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-zinc-900/40">
              {columns.map((c) => (
                <td key={c} className="px-3 py-2 text-zinc-300">
                  {c === "sent_at" ? (r[c] ? r[c].slice(0, 19).replace("T", " ") : "—") : (r[c] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ConfirmModal({ title, message, confirmLabel, onConfirm, onCancel, destructive }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      data-testid="confirm-modal" onClick={onCancel}>
      <div className="w-full max-w-md bg-[#141416] border border-zinc-800 p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            {destructive && <AlertTriangle size={16} className="text-[#EF4444]" />}
            <h3 className="font-display font-black text-lg">{title}</h3>
          </div>
          <button onClick={onCancel} className="text-zinc-500 hover:text-zinc-200" data-testid="confirm-close">
            <X size={16} />
          </button>
        </div>
        <p className="text-sm text-zinc-400 whitespace-pre-line font-mono leading-relaxed">{message}</p>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onCancel} data-testid="confirm-cancel"
            className="px-4 py-2 border border-zinc-800 text-zinc-400 hover:text-zinc-100 hover:border-zinc-600 text-[10px] uppercase tracking-widest font-mono transition-colors">
            Cancel
          </button>
          <button onClick={onConfirm} data-testid="confirm-ok"
            className={`px-4 py-2 text-[10px] uppercase tracking-widest font-mono transition-colors border ${
              destructive
                ? "bg-[#EF4444] text-black border-[#EF4444] hover:bg-[#dc2626]"
                : "bg-[#00E5FF] text-black border-[#00E5FF] hover:bg-[#00b8cc]"
            }`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
